/**
 * Utility functions for managing WhatsApp message sending logic related to retailer users and customers.
 *
 * @module whatsappMessageUtils
 */

/**
 * Checks if a WhatsApp message can be sent based on the retailer user and customer information.
 *
 * @param {Object} retailerUser - The retailer user object containing user information.
 * @param {Object} retailer - The retailer object.
 * @param {Object} customer - The customer object containing customer details.
 * @param {boolean} [isWhatsappConnected] - Estado de conexión ya resuelto (flags legacy +
 *   integraciones del microservicio de inbox), normalmente de `useWhatsappIntegrationStatus`.
 * @returns {boolean} True if a message can be sent, otherwise false.
 */
const canSendWhatsappMsg = (retailerUser, retailer, customer, isWhatsappConnected) => {
  // `retailer.whatsapp_integrated` solo conoce Gupshup/QR legacy: un retailer servido por el
  // microservicio de inbox lo trae en false aunque tenga platform_retailers activos. El call site
  // pasa el estado ya resuelto; el fallback mantiene el comportamiento previo si no llega.
  const whatsappConnected = isWhatsappConnected ?? Boolean(retailer?.whatsapp_integrated);

  if (!hasWhatsappRecipient(customer) || !whatsappConnected) {
    return false;
  }

  if (!customer.retailer_user_id && notOnlyAssigned(retailerUser)) {
    return true;
  }

  const assigned = customer.retailer_user_id === retailerUser.id;

  if (onlyAssigned(retailerUser)) {
    return assigned;
  }

  if (superAdmin(retailerUser)) {
    return true;
  }

  return assignedToTeam(retailerUser, customer) || assigned;
};

/**
 * Checks if the retailer user is only assigned to a customer based on their role.
 *
 * @param {Object} retailerUser - The retailer user object containing user information.
 * @returns {boolean} True if the retailer user is an agent and only assigned, otherwise false.
 */
const onlyAssigned = (retailerUser) => retailerUser.agent && retailerUser.onlyAssigned;

/**
 * Checks if the retailer user is assigned to a team based on their role and customer assignment.
 *
 * @param {Object} retailerUser - The retailer user object containing user information.
 * @param {Object} customer - The customer object containing customer details.
 * @returns {boolean} True if the retailer user is an admin or supervisor and either not assigned to a customer or managed by them, otherwise false.
 */
const assignedToTeam = (retailerUser, customer) => (
  (retailerUser.admin || retailerUser.supervisor)
    && (customer.retailer_user_id === null
      || retailerUser.managed_agents.includes(customer.retailer_user_id))
);

/**
 * Checks if the retailer user is not the only assigned user for a customer.
 *
 * @param {Object} retailerUser - The retailer user object containing user information.
 * @returns {boolean} True if the retailer user is an agent and not the only assigned user, otherwise false.
 */
const notOnlyAssigned = (retailerUser) => retailerUser.agent && !retailerUser.only_assigned;

const hasWhatsappRecipient = (customer) => Boolean(
  customer?.phone || customer?.user_id || customer?.parent_user_id
);

const whatsappRecipientLabel = (customer, labels = {}) => {
  if (!customer) return '';

  if (customer.phone_with_country_code || customer.phone) {
    return customer.phone_with_country_code || customer.phone;
  }

  if (customer.user_id) return labels.userId || '';
  if (customer.parent_user_id) return labels.parentUserId || '';

  return '';
};

/**
 * Checks if the retailer user is a super admin based on their assigned roles.
 *
 * @param {Object} retailerUser - The retailer user object containing user information.
 * @returns {boolean} True if the retailer user is a super admin, otherwise false.
 */
const superAdmin = (retailerUser) => retailerUser.managed_agents.length === 0 && (retailerUser.admin || retailerUser.supervisor);

// Exporting the functions for external use
export {
  canSendWhatsappMsg,
  onlyAssigned,
  assignedToTeam,
  notOnlyAssigned,
  superAdmin,
  hasWhatsappRecipient,
  whatsappRecipientLabel
};
